import { supabase } from "@/integrations/supabase/client";

/**
 * Types for email service parameters
 */
export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  website: string;
  message: string;
  service: string;
}

interface NewsletterSignupData {
  email: string;
}

/**
 * Helper to normalize URLs without strict validation
 */
export const normalizeUrl = (url: string): string => {
  if (!url || url.trim() === "") return "";
  let normalizedUrl = url.trim();
  if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
    normalizedUrl = "https://" + normalizedUrl;
  }
  return normalizedUrl;
};

/**
 * Save a contact form submission directly to the leads table.
 */
export const sendContactForm = async (data: ContactFormData): Promise<boolean> => {
  console.log("Saving contact form lead:", data);

  try {
    const normalizedWebsite = data.website ? normalizeUrl(data.website) : "";

    const { error } = await supabase.from("leads").insert({
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      website: normalizedWebsite || null,
      message: data.message || "No message provided",
      service: data.service,
      source: "contact",
      is_partial: false,
    });

    if (error) {
      console.error("Error saving lead:", error);
      return false;
    }

    sessionStorage.setItem("formSubmitted", "true");
    return true;
  } catch (error) {
    console.error("Unexpected error sending contact form:", error);
    return false;
  }
};

/**
 * Sign up user for the newsletter (saved as a lead).
 */
export const sendNewsletterSignup = async (data: NewsletterSignupData): Promise<boolean> => {
  console.log("Newsletter signup:", data);

  try {
    const { error } = await supabase.from("leads").insert({
      name: "Newsletter Subscriber",
      email: data.email,
      message: "Newsletter subscription",
      service: "Newsletter",
      source: "newsletter",
      is_partial: false,
    });

    if (error) {
      console.error("Newsletter signup error:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Newsletter signup error:", error);
    return false;
  }
};

/**
 * Record a URL test submission as a lead.
 */
export const recordUrlTest = async (url: string): Promise<boolean> => {
  console.log("URL test submitted:", url);

  try {
    const normalizedUrl = normalizeUrl(url);

    const { error } = await supabase.from("leads").insert({
      name: "SEO Tool User",
      email: "unknown@seo-tool.user",
      website: normalizedUrl,
      message: `URL test requested for: ${normalizedUrl}`,
      service: "SEO Tool",
      source: "url_test",
      is_partial: false,
    });

    if (error) {
      console.error("URL test record error:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("URL test record error:", error);
    return false;
  }
};
